import React, { useState } from "react";
import {
    BrowserRouter as Router,
    Routes,
    Route,
    useNavigate,
} from "react-router-dom";
import Layout from "./components/Layout";
import { useAuth0 } from "@auth0/auth0-react";
import { TripsList } from "./components/TripsList";
import TripForm from "./components/TripForm";
import TripDetails from "./components/TripDetails";
import ContactsPage from "./components/Contacts";
import ItemsPage from "./components/Items";
import About from "./components/About";
import Footer from "./components/Footer";
import Privacy from "./components/Privacy";
import Terms from "./components/Terms";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

type LandingInlineProps = {
    onLogin: () => void;
};

// Landing page shown to unauthenticated visitors
function LandingInline({ onLogin }: LandingInlineProps) {
    return (
        <>
            <style>
                {`
                    .trip-landing {
                        --landing-primary: #4f46e5;
                        --landing-primary-dark: #4338ca;
                        --landing-primary-light: #eef2ff;
                        --landing-secondary: #0ea5e9;
                        --landing-success: #15803d;
                        --landing-text: #0f172a;
                        --landing-muted: #64748b;
                        --landing-border: #e2e8f0;
                        color: var(--landing-text);
                    }

                    .trip-landing * {
                        box-sizing: border-box;
                    }

                    .trip-landing-container {
                        width: min(1180px, calc(100% - 40px));
                        margin: 0 auto;
                    }

                    .trip-landing-hero {
                        position: relative;
                        overflow: hidden;
                        padding: 78px 0 86px;
                        background:
                            radial-gradient(
                                circle at 88% 8%,
                                rgba(14, 165, 233, 0.16),
                                transparent 30%
                            ),
                            radial-gradient(
                                circle at 8% 12%,
                                rgba(79, 70, 229, 0.14),
                                transparent 34%
                            ),
                            #ffffff;
                    }

                    .trip-landing-hero-grid {
                        display: grid;
                        grid-template-columns: minmax(0, 1.08fr) minmax(380px, 0.92fr);
                        align-items: center;
                        gap: 58px;
                    }

                    .trip-landing-eyebrow {
                        display: inline-flex;
                        align-items: center;
                        margin-bottom: 18px;
                        padding: 7px 13px;
                        border-radius: 999px;
                        background: var(--landing-primary-light);
                        color: var(--landing-primary-dark);
                        font-size: 13px;
                        font-weight: 800;
                        letter-spacing: 0.045em;
                        text-transform: uppercase;
                    }

                    .trip-landing-title {
                        max-width: 720px;
                        margin: 0 0 20px;
                        font-size: clamp(42px, 5.4vw, 66px);
                        line-height: 1.04;
                        letter-spacing: -0.045em;
                    }

                    .trip-landing-lead {
                        max-width: 690px;
                        margin: 0 0 28px;
                        color: var(--landing-muted);
                        font-size: 19px;
                        line-height: 1.7;
                    }

                    .trip-landing-actions {
                        display: flex;
                        flex-wrap: wrap;
                        gap: 12px;
                        margin-bottom: 26px;
                    }

                    .trip-landing-button {
                        display: inline-flex;
                        min-height: 48px;
                        align-items: center;
                        justify-content: center;
                        padding: 12px 22px;
                        border: 0;
                        border-radius: 10px;
                        background: var(--landing-primary);
                        color: #fff;
                        font: inherit;
                        font-weight: 750;
                        text-decoration: none;
                        cursor: pointer;
                        transition: 0.15s ease;
                    }

                    .trip-landing-button:hover {
                        background: var(--landing-primary-dark);
                        color: #fff;
                        transform: translateY(-1px);
                        box-shadow: 0 10px 25px rgba(79, 70, 229, 0.22);
                    }

                    .trip-landing-button-secondary {
                        border: 1px solid #c7d2fe;
                        background: #fff;
                        color: var(--landing-primary);
                    }

                    .trip-landing-button-secondary:hover {
                        background: var(--landing-primary-light);
                        color: var(--landing-primary-dark);
                        box-shadow: none;
                    }

                    .trip-landing-points {
                        display: flex;
                        flex-wrap: wrap;
                        gap: 10px 22px;
                        color: var(--landing-muted);
                        font-size: 14px;
                        font-weight: 650;
                    }

                    .trip-landing-points span::before {
                        content: "✓";
                        margin-right: 7px;
                        color: var(--landing-success);
                        font-weight: 900;
                    }

                    .trip-preview {
                        padding: 20px;
                        border: 1px solid rgba(226, 232, 240, 0.9);
                        border-radius: 20px;
                        background: rgba(255, 255, 255, 0.96);
                        box-shadow: 0 22px 60px rgba(15, 23, 42, 0.12);
                    }

                    .trip-preview-header {
                        display: flex;
                        justify-content: space-between;
                        gap: 16px;
                        margin-bottom: 18px;
                    }

                    .trip-preview-name {
                        font-size: 19px;
                        font-weight: 800;
                    }

                    .trip-preview-meta {
                        color: var(--landing-muted);
                        font-size: 12px;
                    }

                    .trip-preview-status {
                        padding: 6px 10px;
                        border-radius: 999px;
                        background: #dcfce7;
                        color: #166534;
                        font-size: 12px;
                        font-weight: 800;
                        white-space: nowrap;
                    }

                    .trip-preview-summary {
                        display: grid;
                        grid-template-columns: repeat(3, 1fr);
                        gap: 10px;
                        margin-bottom: 18px;
                    }

                    .trip-preview-summary-box {
                        padding: 13px;
                        border: 1px solid var(--landing-border);
                        border-radius: 12px;
                        background: #f8fafc;
                    }

                    .trip-preview-summary-label {
                        display: block;
                        margin-bottom: 3px;
                        color: var(--landing-muted);
                        font-size: 10px;
                        font-weight: 750;
                        letter-spacing: 0.04em;
                        text-transform: uppercase;
                    }

                    .trip-preview-summary-value {
                        display: block;
                        font-size: 17px;
                        font-weight: 850;
                    }

                    .trip-preview-section-title {
                        margin: 18px 0 6px;
                        color: var(--landing-muted);
                        font-size: 11px;
                        font-weight: 800;
                        letter-spacing: 0.05em;
                        text-transform: uppercase;
                    }

                    .trip-preview-row {
                        display: grid;
                        grid-template-columns: 1fr auto;
                        gap: 14px;
                        padding: 12px 0;
                        border-bottom: 1px solid var(--landing-border);
                    }

                    .trip-preview-row:last-child {
                        border-bottom: 0;
                    }

                    .trip-preview-row-title {
                        font-size: 14px;
                        font-weight: 750;
                    }

                    .trip-preview-row-meta {
                        color: var(--landing-muted);
                        font-size: 12px;
                    }

                    .trip-preview-row-amount {
                        font-size: 14px;
                        font-weight: 800;
                    }

                    .trip-currency-chip {
                        display: inline-flex;
                        align-items: center;
                        gap: 5px;
                        margin-left: 7px;
                        padding: 3px 7px;
                        border-radius: 999px;
                        background: #eef2ff;
                        color: #4338ca;
                        font-size: 10px;
                        font-weight: 800;
                    }

                    .trip-saved-item {
                        display: flex;
                        align-items: center;
                        gap: 10px;
                        margin-top: 10px;
                        padding: 10px 11px;
                        border: 1px solid #e2e8f0;
                        border-radius: 10px;
                        background: #f8fafc;
                    }

                    .trip-saved-item-icon {
                        width: 30px;
                        height: 30px;
                        display: grid;
                        place-items: center;
                        border-radius: 8px;
                        background: #eef2ff;
                        color: #4f46e5;
                        font-size: 11px;
                        font-weight: 900;
                    }

                    .trip-saved-item-text {
                        flex: 1;
                        min-width: 0;
                    }

                    .trip-saved-item-name {
                        font-size: 12px;
                        font-weight: 800;
                    }

                    .trip-saved-item-meta {
                        color: var(--landing-muted);
                        font-size: 10px;
                    }

                    .trip-landing-section {
                        padding: 78px 0;
                    }

                    .trip-landing-section-light {
                        background: #f8fafc;
                    }

                    .trip-landing-section-heading {
                        max-width: 760px;
                        margin-bottom: 34px;
                    }

                    .trip-landing-section-heading h2,
                    .trip-collaboration-copy h2 {
                        margin: 0 0 14px;
                        font-size: clamp(30px, 4vw, 43px);
                        line-height: 1.15;
                        letter-spacing: -0.035em;
                    }

                    .trip-landing-section-heading p,
                    .trip-collaboration-copy > p {
                        margin: 0;
                        color: var(--landing-muted);
                        font-size: 17px;
                        line-height: 1.7;
                    }

                    .trip-landing-steps {
                        display: grid;
                        grid-template-columns: repeat(4, 1fr);
                        gap: 18px;
                    }

                    .trip-landing-features {
                        display: grid;
                        grid-template-columns: repeat(3, 1fr);
                        gap: 18px;
                    }

                    .trip-landing-card {
                        padding: 25px;
                        border: 1px solid var(--landing-border);
                        border-radius: 16px;
                        background: #fff;
                        box-shadow: 0 10px 28px rgba(15, 23, 42, 0.045);
                    }

                    .trip-landing-card h3 {
                        margin: 0 0 10px;
                        font-size: 19px;
                    }

                    .trip-landing-card p {
                        margin: 0;
                        color: var(--landing-muted);
                        line-height: 1.65;
                    }

                    .trip-step-number,
                    .trip-feature-icon {
                        width: 42px;
                        height: 42px;
                        display: grid;
                        place-items: center;
                        margin-bottom: 17px;
                        border-radius: 11px;
                        background: var(--landing-primary-light);
                        color: var(--landing-primary-dark);
                        font-weight: 850;
                    }

                    .trip-feature-icon {
                        width: max-content;
                        min-width: 42px;
                        padding: 0 12px;
                        font-size: 11px;
                        letter-spacing: 0.03em;
                        text-transform: uppercase;
                    }

                    .trip-currency-feature {
                        border-color: #c7d2fe;
                        background:
                            linear-gradient(
                                145deg,
                                #ffffff,
                                #eef2ff
                            );
                    }

                    .trip-saved-feature {
                        border-color: #bae6fd;
                        background:
                            linear-gradient(
                                145deg,
                                #ffffff,
                                #f0f9ff
                            );
                    }

                    .trip-collaboration {
                        display: grid;
                        grid-template-columns: 1fr 1fr;
                        align-items: center;
                        gap: 48px;
                    }

                    .trip-check-list {
                        margin: 25px 0 0;
                        padding: 0;
                        list-style: none;
                    }

                    .trip-check-list li {
                        position: relative;
                        margin-bottom: 13px;
                        padding-left: 29px;
                        color: var(--landing-muted);
                    }

                    .trip-check-list li::before {
                        content: "✓";
                        position: absolute;
                        left: 0;
                        color: var(--landing-success);
                        font-weight: 900;
                    }

                    .trip-notification-panel {
                        padding: 24px;
                        border-radius: 18px;
                        background: #111827;
                        color: #fff;
                        box-shadow: 0 22px 55px rgba(15, 23, 42, 0.18);
                    }

                    .trip-notification {
                        display: flex;
                        gap: 13px;
                        margin-bottom: 12px;
                        padding: 15px;
                        border-radius: 12px;
                        background: rgba(255, 255, 255, 0.08);
                    }

                    .trip-notification:last-child {
                        margin-bottom: 0;
                    }

                    .trip-notification-icon {
                        flex: 0 0 auto;
                        width: 38px;
                        height: 38px;
                        display: grid;
                        place-items: center;
                        border-radius: 10px;
                        background: rgba(129, 140, 248, 0.24);
                        color: #e0e7ff;
                        font-size: 10px;
                        font-weight: 850;
                    }

                    .trip-notification strong {
                        display: block;
                        margin-bottom: 3px;
                    }

                    .trip-notification small {
                        color: #cbd5e1;
                        line-height: 1.55;
                    }

                    .trip-landing-final {
                        padding: 74px 0;
                    }

                    .trip-final-panel {
                        display: flex;
                        align-items: center;
                        justify-content: space-between;
                        gap: 30px;
                        padding: 46px;
                        border-radius: 22px;
                        background:
                            linear-gradient(
                                135deg,
                                #312e81,
                                var(--landing-primary),
                                var(--landing-secondary)
                            );
                        color: #fff;
                        box-shadow: 0 22px 60px rgba(79, 70, 229, 0.2);
                    }

                    .trip-final-panel h2 {
                        margin: 0 0 10px;
                        font-size: clamp(28px, 4vw, 40px);
                        line-height: 1.2;
                    }

                    .trip-final-panel p {
                        max-width: 650px;
                        margin: 0;
                        color: #e0e7ff;
                        line-height: 1.7;
                    }

                    .trip-final-panel .trip-landing-button {
                        flex: 0 0 auto;
                        background: #fff;
                        color: var(--landing-primary-dark);
                    }

                    @media (max-width: 980px) {
                        .trip-landing-hero-grid,
                        .trip-collaboration {
                            grid-template-columns: 1fr;
                        }

                        .trip-landing-steps {
                            grid-template-columns: repeat(2, 1fr);
                        }

                        .trip-landing-features {
                            grid-template-columns: repeat(2, 1fr);
                        }
                    }

                    @media (max-width: 680px) {
                        .trip-landing-container {
                            width: min(100% - 28px, 1180px);
                        }

                        .trip-landing-hero {
                            padding: 48px 0 56px;
                        }

                        .trip-landing-title {
                            font-size: 41px;
                        }

                        .trip-landing-lead {
                            font-size: 17px;
                        }

                        .trip-landing-actions,
                        .trip-landing-actions .trip-landing-button {
                            width: 100%;
                        }

                        .trip-preview-summary,
                        .trip-landing-steps,
                        .trip-landing-features {
                            grid-template-columns: 1fr;
                        }

                        .trip-landing-section {
                            padding: 58px 0;
                        }

                        .trip-final-panel {
                            flex-direction: column;
                            align-items: flex-start;
                            padding: 32px 24px;
                        }

                        .trip-final-panel .trip-landing-button {
                            width: 100%;
                        }
                    }
                `}
            </style>

            <div className="trip-landing">

                <section className="trip-landing-hero">
                    <div className="trip-landing-container trip-landing-hero-grid">

                        <div>
                            <span className="trip-landing-eyebrow">
                                Group travel without the spreadsheet
                            </span>

                            <h1 className="trip-landing-title">
                                Enjoy the trip.
                                <br />
                                We’ll handle the math.
                            </h1>

                            <p className="trip-landing-lead">
                                TripSplitter makes group expenses simple. Track who paid,
                                split purchases between the right people, use multiple
                                currencies, and see exactly how the trip settles.
                            </p>

                            <div className="trip-landing-actions">
                                <button
                                    type="button"
                                    className="trip-landing-button"
                                    onClick={onLogin}
                                >
                                    Create Your First Trip
                                </button>

                                <a
                                    className="trip-landing-button trip-landing-button-secondary"
                                    href="#trip-features"
                                >
                                    Explore Features
                                </a>
                            </div>

                            <div className="trip-landing-points">
                                <span>Multiple currencies</span>
                                <span>Saved frequent expenses</span>
                                <span>Flexible sharing</span>
                                <span>Automatic settlements</span>
                            </div>
                        </div>

                        <aside className="trip-preview">

                            <div className="trip-preview-header">
                                <div>
                                    <div className="trip-preview-name">
                                        Vancouver Weekend
                                    </div>

                                    <div className="trip-preview-meta">
                                        4 participants · 8 expenses
                                    </div>
                                </div>

                                <span className="trip-preview-status">
                                    Ready to settle
                                </span>
                            </div>

                            <div className="trip-preview-summary">

                                <div className="trip-preview-summary-box">
                                    <span className="trip-preview-summary-label">
                                        Trip total
                                    </span>
                                    <span className="trip-preview-summary-value">
                                        $842.50 CAD
                                    </span>
                                </div>

                                <div className="trip-preview-summary-box">
                                    <span className="trip-preview-summary-label">
                                        Currencies
                                    </span>
                                    <span className="trip-preview-summary-value">
                                        CAD · USD
                                    </span>
                                </div>

                                <div className="trip-preview-summary-box">
                                    <span className="trip-preview-summary-label">
                                        Settlement
                                    </span>
                                    <span className="trip-preview-summary-value">
                                        2 payments
                                    </span>
                                </div>

                            </div>

                            <div className="trip-preview-section-title">
                                Recent expenses
                            </div>

                            <div className="trip-preview-row">
                                <div>
                                    <div className="trip-preview-row-title">
                                        Hotel
                                        <span className="trip-currency-chip">
                                            CAD
                                        </span>
                                    </div>

                                    <div className="trip-preview-row-meta">
                                        Paid by Maya · Everyone
                                    </div>
                                </div>

                                <div className="trip-preview-row-amount">
                                    $480.00
                                </div>
                            </div>

                            <div className="trip-preview-row">
                                <div>
                                    <div className="trip-preview-row-title">
                                        Dinner
                                        <span className="trip-currency-chip">
                                            USD
                                        </span>
                                    </div>

                                    <div className="trip-preview-row-meta">
                                        Paid by Alex · Everyone
                                    </div>
                                </div>

                                <div className="trip-preview-row-amount">
                                    US$186.50
                                </div>
                            </div>

                            <div className="trip-preview-row">
                                <div>
                                    <div className="trip-preview-row-title">
                                        Museum tickets
                                        <span className="trip-currency-chip">
                                            CAD
                                        </span>
                                    </div>

                                    <div className="trip-preview-row-meta">
                                        Paid by Jordan · 3 people
                                    </div>
                                </div>

                                <div className="trip-preview-row-amount">
                                    $96.00
                                </div>
                            </div>

                            <div className="trip-preview-section-title">
                                Frequently used expenses
                            </div>

                            <div className="trip-saved-item">
                                <div className="trip-saved-item-icon">
                                    +
                                </div>

                                <div className="trip-saved-item-text">
                                    <div className="trip-saved-item-name">
                                        Hotel
                                    </div>

                                    <div className="trip-saved-item-meta">
                                        Saved expense · CAD
                                    </div>
                                </div>

                                <strong>$480</strong>
                            </div>

                            <div className="trip-saved-item">
                                <div className="trip-saved-item-icon">
                                    +
                                </div>

                                <div className="trip-saved-item-text">
                                    <div className="trip-saved-item-name">
                                        Dinner
                                    </div>

                                    <div className="trip-saved-item-meta">
                                        Saved expense · USD
                                    </div>
                                </div>

                                <strong>US$185</strong>
                            </div>

                        </aside>
                    </div>
                </section>

                <section
                    id="trip-features"
                    className="trip-landing-section trip-landing-section-light"
                >
                    <div className="trip-landing-container">

                        <div className="trip-landing-section-heading">
                            <span className="trip-landing-eyebrow">
                                Built for real trips
                            </span>

                            <h2>
                                Everything you need to keep group expenses under control
                            </h2>

                            <p>
                                From the first coffee to the final settlement,
                                TripSplitter keeps the financial side of a group trip
                                organized and easy to understand.
                            </p>
                        </div>

                        <div className="trip-landing-features">

                            <article className="trip-landing-card">
                                <div className="trip-feature-icon">
                                    Trips
                                </div>

                                <h3>
                                    Keep every trip organized
                                </h3>

                                <p>
                                    Create separate trips with their own participants,
                                    expenses, dates, currencies, and settlement totals.
                                </p>
                            </article>

                            <article className="trip-landing-card trip-currency-feature">
                                <div className="trip-feature-icon">
                                    FX
                                </div>

                                <h3>
                                    Travel across currencies
                                </h3>

                                <p>
                                    Enter an expense in the currency you actually paid.
                                    TripSplitter keeps the original amount while
                                    calculating its value against the trip’s base currency.
                                </p>
                            </article>

                            <article className="trip-landing-card trip-saved-feature">
                                <div className="trip-feature-icon">
                                    Items
                                </div>

                                <h3>
                                    Save expenses you use again
                                </h3>

                                <p>
                                    Save common expenses such as Hotel, Gas, Dinner,
                                    Groceries, or Ferry tickets. Next time, add them
                                    without typing everything again.
                                </p>
                            </article>

                            <article className="trip-landing-card">
                                <div className="trip-feature-icon">
                                    Split
                                </div>

                                <h3>
                                    Share expenses your way
                                </h3>

                                <p>
                                    Choose exactly who participated in each expense
                                    instead of forcing every purchase to be split
                                    between everyone.
                                </p>
                            </article>

                            <article className="trip-landing-card">
                                <div className="trip-feature-icon">
                                    Math
                                </div>

                                <h3>
                                    Automatic calculations
                                </h3>

                                <p>
                                    TripSplitter calculates participant balances and
                                    shows the payments needed to settle the trip.
                                </p>
                            </article>

                            <article className="trip-landing-card">
                                <div className="trip-feature-icon">
                                    Email
                                </div>

                                <h3>
                                    Keep everyone informed
                                </h3>

                                <p>
                                    Notify participants when they are added, remind
                                    them to enter expenses, and send the final summary
                                    when the trip is complete.
                                </p>
                            </article>

                        </div>
                    </div>
                </section>

                <section className="trip-landing-section">
                    <div className="trip-landing-container">

                        <div className="trip-landing-section-heading">
                            <span className="trip-landing-eyebrow">
                                How it works
                            </span>

                            <h2>
                                Four simple steps from spending to settling
                            </h2>
                        </div>

                        <div className="trip-landing-steps">

                            <article className="trip-landing-card">
                                <div className="trip-step-number">1</div>

                                <h3>Create your trip</h3>

                                <p>
                                    Give your trip a name, choose its base currency,
                                    and set the dates.
                                </p>
                            </article>

                            <article className="trip-landing-card">
                                <div className="trip-step-number">2</div>

                                <h3>Add your group</h3>

                                <p>
                                    Add the friends or family members who are sharing
                                    the trip expenses.
                                </p>
                            </article>

                            <article className="trip-landing-card">
                                <div className="trip-step-number">3</div>

                                <h3>Add expenses</h3>

                                <p>
                                    Enter purchases in their original currency or
                                    quickly select a frequently used saved item.
                                </p>
                            </article>

                            <article className="trip-landing-card">
                                <div className="trip-step-number">4</div>

                                <h3>Settle up</h3>

                                <p>
                                    Review balances and the payments needed to bring
                                    everyone back into balance.
                                </p>
                            </article>

                        </div>
                    </div>
                </section>

                <section className="trip-landing-section trip-landing-section-light">
                    <div className="trip-landing-container trip-collaboration">

                        <div className="trip-collaboration-copy">

                            <span className="trip-landing-eyebrow">
                                Less typing. Less chasing.
                            </span>

                            <h2>
                                Your repeat expenses are already waiting
                            </h2>

                            <p>
                                Trips have a lot of predictable expenses. Instead of
                                repeatedly typing the same information, save the things
                                you buy often and reuse them whenever you need them.
                            </p>

                            <ul className="trip-check-list">
                                <li>
                                    Save Hotel, Gas, Dinner, Groceries, Ferry, or anything else.
                                </li>

                                <li>
                                    Give each saved item its own currency.
                                </li>

                                <li>
                                    Optionally save a default amount.
                                </li>

                                <li>
                                    Select the item when adding a new expense.
                                </li>

                                <li>
                                    Change the amount whenever the actual purchase is different.
                                </li>
                            </ul>

                        </div>

                        <div className="trip-preview">

                            <div className="trip-preview-section-title">
                                Your saved expenses
                            </div>

                            <div className="trip-saved-item">
                                <div className="trip-saved-item-icon">
                                    $
                                </div>

                                <div className="trip-saved-item-text">
                                    <div className="trip-saved-item-name">
                                        Hotel
                                    </div>

                                    <div className="trip-saved-item-meta">
                                        CAD · Default $250.00
                                    </div>
                                </div>

                                <strong>Use</strong>
                            </div>

                            <div className="trip-saved-item">
                                <div className="trip-saved-item-icon">
                                    $
                                </div>

                                <div className="trip-saved-item-text">
                                    <div className="trip-saved-item-name">
                                        Gas
                                    </div>

                                    <div className="trip-saved-item-meta">
                                        CAD · Default $80.00
                                    </div>
                                </div>

                                <strong>Use</strong>
                            </div>

                            <div className="trip-saved-item">
                                <div className="trip-saved-item-icon">
                                    $
                                </div>

                                <div className="trip-saved-item-text">
                                    <div className="trip-saved-item-name">
                                        Dinner
                                    </div>

                                    <div className="trip-saved-item-meta">
                                        USD · Default US$120.00
                                    </div>
                                </div>

                                <strong>Use</strong>
                            </div>

                            <div className="trip-saved-item">
                                <div className="trip-saved-item-icon">
                                    $
                                </div>

                                <div className="trip-saved-item-text">
                                    <div className="trip-saved-item-name">
                                        Groceries
                                    </div>

                                    <div className="trip-saved-item-meta">
                                        CAD · No default amount
                                    </div>
                                </div>

                                <strong>Use</strong>
                            </div>

                        </div>
                    </div>
                </section>

                <section className="trip-landing-section">
                    <div className="trip-landing-container trip-collaboration">

                        <div className="trip-collaboration-copy">

                            <span className="trip-landing-eyebrow">
                                Keep the group moving
                            </span>

                            <h2>
                                Everyone knows what still needs to happen
                            </h2>

                            <p>
                                TripSplitter helps the organizer keep the trip moving
                                without becoming the group accountant.
                            </p>

                            <ul className="trip-check-list">
                                <li>
                                    Notify people when they are added to the trip.
                                </li>

                                <li>
                                    Remind participants to enter their expenses.
                                </li>

                                <li>
                                    Track who has finished entering expenses.
                                </li>

                                <li>
                                    Review the complete trip before settling.
                                </li>

                                <li>
                                    Send the final summary to the group.
                                </li>
                            </ul>

                        </div>

                        <div className="trip-notification-panel">

                            <div className="trip-notification">
                                <div className="trip-notification-icon">
                                    ADD
                                </div>

                                <div>
                                    <strong>
                                        You were added to a trip
                                    </strong>

                                    <small>
                                        Maya invited you to Vancouver Weekend.
                                        Add your expenses when you're ready.
                                    </small>
                                </div>
                            </div>

                            <div className="trip-notification">
                                <div className="trip-notification-icon">
                                    REM
                                </div>

                                <div>
                                    <strong>
                                        Expense reminder
                                    </strong>

                                    <small>
                                        The group is almost finished. Add any
                                        remaining purchases and mark yourself done.
                                    </small>
                                </div>
                            </div>

                            <div className="trip-notification">
                                <div className="trip-notification-icon">
                                    DONE
                                </div>

                                <div>
                                    <strong>
                                        Trip summary ready
                                    </strong>

                                    <small>
                                        Everyone has finished. Review the balances
                                        and final settlement instructions.
                                    </small>
                                </div>
                            </div>

                        </div>
                    </div>
                </section>

                <section className="trip-landing-final">
                    <div className="trip-landing-container">

                        <div className="trip-final-panel">

                            <div>
                                <h2>
                                    Spend your time on the trip, not the spreadsheet.
                                </h2>

                                <p>
                                    Create your first trip, invite your group,
                                    and let TripSplitter take care of the expense math.
                                </p>
                            </div>

                            <button
                                type="button"
                                className="trip-landing-button"
                                onClick={onLogin}
                            >
                                Start Splitting
                            </button>

                        </div>
                    </div>
                </section>

            </div>
        </>
    );
}


function App() {
    const {
        isAuthenticated,
        isLoading,
        getAccessTokenSilently,
        loginWithRedirect,
    } = useAuth0();

    const [refreshTrips, setRefreshTrips] = useState(false);
    const navigate = useNavigate();

    if (isLoading) {
        return <div>Loading...</div>;
    }

    const getToken = async () => await getAccessTokenSilently();

    const handleTripAdded = () => {
        setRefreshTrips((previous) => !previous);
    };

    const handleViewDetails = (tripId: string) => {
        navigate(`/trip/${tripId}`);
    };

    return (
        <>
            <Layout>
                <Routes>
                    <Route
                        path="/"
                        element={
                            isAuthenticated ? (
                                <>
                                    <TripForm
                                        getToken={getToken}
                                        onTripAdded={handleTripAdded}
                                    />

                                    <TripsList
                                        key={refreshTrips.toString()}
                                        getToken={getToken}
                                        onViewDetails={handleViewDetails}
                                    />
                                </>
                            ) : (
                                <LandingInline
                                    onLogin={() => {
                                        void loginWithRedirect();
                                    }}
                                />
                            )
                        }
                    />

                    <Route path="/trip/:id" element={<TripDetails />} />
                    <Route path="/about" element={<About />} />
                    <Route path="/privacy" element={<Privacy />} />
                    <Route path="/terms" element={<Terms />} />
                    <Route path="/contacts" element={<ContactsPage />} />
                    <Route path="/items" element={<ItemsPage />} />
                </Routes>
            </Layout>

            <Footer />
        </>
    );
}

export default function AppWithRouter() {
    return (
        <Router>
            <App />
            <ToastContainer position="top-right" autoClose={3000} />
        </Router>
    );
}